import React from 'react'

import styled from '@emotion/styled'

import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined'
import { Button, Form, Input, message, Checkbox, Badge, Tooltip, Row, Col, Space } from 'antd'

import { observer } from 'mobx-react-lite'

import { Box } from 'common/components/boxes'
import { Page } from 'common/components/Page'
import { TextArea } from 'common/components/TextArea'

import { useVendorStores } from 'vendor/hooks/use-stores'

interface UserPersonalInformationPayload {
  first_name: string
  last_name: string
  is_public: boolean
  headline?: string
}

const FormItemWithIcon = styled(Form.Item)`
  margin-top: 8px;
  .ant-form-item-label {
    overflow: unset;
  }
`

export const PersonalInformation = observer(() => {
  const { userStore } = useVendorStores()

  const [form] = Form.useForm()

  const user = userStore.currentUser

  const onSubmit = async (values: UserPersonalInformationPayload) => {
    try {
      await userStore.updatePersonalInformation(values)
      message.success('Personal information updated successfully')
    } catch (err) {
      console.error('Unable to update', err)
      message.error('Unable to update your personal information')
    }
  }

  if (!user) {
    return null
  }

  return (
    <>
      <Page.Header p={8} />

      <Page.Content>
        <Form<UserPersonalInformationPayload>
          initialValues={{
            first_name: user?.first_name,
            last_name: user?.last_name,
            is_public: user?.is_public,
            headline: user?.headline,
          }}
          layout="vertical"
          form={form}
          onFinish={onSubmit}
          style={{ width: '100%' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="first_name"
                label="First Name"
                rules={[{ required: true, message: 'First name is required' }]}
              >
                <Input style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="last_name"
                label="Last Name"
                rules={[{ required: true, message: 'Last name is required' }]}
              >
                <Input style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <FormItemWithIcon
                name="headline"
                label={
                  <Badge
                    count={
                      <Tooltip placement="top" title="Short headline that shows up for users to identify who you are">
                        <QuestionCircleOutlined style={{ top: -5, right: -5 }} />
                      </Tooltip>
                    }
                  >
                    <span>Headline</span>
                  </Badge>
                }
              >
                <TextArea
                  showCount
                  rows={3}
                  maxLength={150}
                  placeholder="Sales for South East Florida"
                  style={{ resize: 'none' }}
                />
              </FormItemWithIcon>
            </Col>

            <Col xs={24} sm={12}>
              <FormItemWithIcon
                valuePropName="checked"
                name="is_public"
                label={
                  <Badge
                    count={
                      <Tooltip
                        placement="top"
                        title="Do you want your email to be public for contractors on Subbase to discover?"
                      >
                        <QuestionCircleOutlined style={{ top: -5, right: -5 }} />
                      </Tooltip>
                    }
                  >
                    <span>Public</span>
                  </Badge>
                }
              >
                <Checkbox />
              </FormItemWithIcon>
            </Col>

            <Col span={24}>
              <Box display="flex" justifyContent="flex-end" width="100%">
                <Space>
                  <Button onClick={() => form.resetFields()}>Cancel</Button>

                  <Button type="primary" htmlType="submit">
                    Update
                  </Button>
                </Space>
              </Box>
            </Col>
          </Row>
        </Form>
      </Page.Content>
    </>
  )
})
